/**
 * DL-01 — the download manifest (story §A-1 / §A-2), read from lyse/v1
 * (8.7.0, pinned in `contracts.lock.json`):
 *
 *   GET /data-acquisitions/{id}/download-manifest           → DownloadManifest
 *   GET /inspection-instructions/{id}/download-manifests    → DownloadManifestSet
 *   GET /projects/{id}/download-manifests                   → DownloadManifestSet
 *
 *   413 DownloadTooLarge · 403 DownloadForbidden (`errors.ts`)
 *
 * The shapes are the generated ones (`@aeriosiq/types`, `api.lyse.gen.ts`).
 * The one thing normalised on the way in is the image `md5`: the contract
 * types it as a string (hex); `md5ToHex` makes it lower-case hex and treats
 * an unusable value as "no md5" (size-only verification), so the resume
 * rule compares like with like. The client never caches a manifest across
 * sessions (§A-6) — the SAS URLs are bucket-expiring; an expired one is
 * re-requested by the run.
 */
import type { components } from "@aeriosiq/types";
import { md5ToHex } from "./md5.js";

type LyseSchemas = components["schemas"];

export type DownloadScopeKind = "da" | "instruction" | "project";

/** What the user asked to download — ONE data acquisition, every set of an
 *  instruction, or every set of a project. `label` is only for the words. */
export interface DownloadScope {
  kind: DownloadScopeKind;
  id: string;
  label?: string | undefined;
}

export type ManifestImage = LyseSchemas["DownloadManifestImage"];
export type ManifestSkipped = LyseSchemas["DownloadManifestSkipped"];
export type DownloadManifest = LyseSchemas["DownloadManifest"];
/** The instruction / project envelope: one manifest per DA (§A-2). */
export type DownloadManifestSet = LyseSchemas["DownloadManifestSet"];
export type ManifestWarning = DownloadManifest["warnings"][number];
/** DL-04 — lyse/v1 8.8.0 `getExportBundle`, written as `bundle.json`. */
export type ExportBundle = LyseSchemas["ExportBundle"];

/** What the dialog works on, whatever the scope: the manifests + the sums. */
export interface ManifestBundle {
  scope: DownloadScope;
  manifests: DownloadManifest[];
  totalBytes: number;
  imageCount: number;
  skippedCount: number;
  warnings: ManifestWarning[];
}

/** The slice of the host axios client the reads need (a test passes a stub). */
export interface ManifestClient {
  get: (url: string) => Promise<{ data: unknown }>;
}

/** The served manifest with its md5s as lower-case hex (or null). */
export function normalizeManifest(m: DownloadManifest): DownloadManifest {
  return {
    ...m,
    images: m.images.map((i) => ({ ...i, md5: md5ToHex(i.md5) })),
  };
}

/** The bundle of a DA scope (one manifest) or a set (several). */
export function bundleOf(
  scope: DownloadScope,
  manifests: DownloadManifest[],
  set?: Pick<DownloadManifestSet, "totalBytes" | "imageCount" | "warnings">,
): ManifestBundle {
  const totalBytes =
    set?.totalBytes ?? manifests.reduce((n, m) => n + m.totalBytes, 0);
  const imageCount =
    set?.imageCount ?? manifests.reduce((n, m) => n + m.imageCount, 0);
  const warnings = new Set<ManifestWarning>(set?.warnings ?? []);
  for (const m of manifests) for (const w of m.warnings) warnings.add(w);
  return {
    scope,
    manifests,
    totalBytes,
    imageCount,
    skippedCount: manifests.reduce((n, m) => n + m.skipped.length, 0),
    warnings: [...warnings],
  };
}

/** The lyse/v1 path of a scope's manifest read (relative to the host client's base). */
export function manifestPathOf(scope: DownloadScope): string {
  const id = encodeURIComponent(scope.id);
  switch (scope.kind) {
    case "da":
      return `/data-acquisitions/${id}/download-manifest`;
    case "instruction":
      return `/inspection-instructions/${id}/download-manifests`;
    case "project":
      return `/projects/${id}/download-manifests`;
  }
}

/**
 * ONE data set's manifest, fresh — the expired-URL renewal (§A-6) asks for
 * the DA whose links expired, by the `dataAcquisitionId` its manifest carries.
 */
export async function requestDaManifest(
  api: ManifestClient,
  dataAcquisitionId: string,
): Promise<DownloadManifest> {
  const res = await api.get(
    manifestPathOf({ kind: "da", id: dataAcquisitionId }),
  );
  return normalizeManifest(res.data as DownloadManifest);
}

/**
 * The manifests of a scope — ONE request. Throws the client's error (an
 * axios error for a 413 / 403); `classifyManifestError` reads it.
 */
export async function requestManifests(
  api: ManifestClient,
  scope: DownloadScope,
): Promise<ManifestBundle> {
  const res = await api.get(manifestPathOf(scope));
  if (scope.kind === "da") {
    return bundleOf(scope, [normalizeManifest(res.data as DownloadManifest)]);
  }
  const set = res.data as DownloadManifestSet;
  return bundleOf(scope, set.manifests.map(normalizeManifest), set);
}

/**
 * DL-04 — the data set's `bundle.json` 2.0 (lyse/v1 8.8.0 `getExportBundle`,
 * platform admin only — any other role gets a 403 the run reports as a
 * failed `bundle.json`).
 */
export async function requestExportBundle(
  api: ManifestClient,
  dataAcquisitionId: string,
): Promise<ExportBundle> {
  const res = await api.get(
    `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/export-bundle`,
  );
  return res.data as ExportBundle;
}
